import { Link } from "react-router-dom";
import { Crown, Mic, Gamepad2, AudioWaveform, ArrowRight, Sparkles } from "lucide-react";

const PROMO_CSS = `
@keyframes medusaPromoFloat { 0%,100% { transform: translateY(0) rotate(-1deg) } 50% { transform: translateY(-10px) rotate(1deg) } }
@keyframes medusaPromoGlow {
  0%,100% { filter: drop-shadow(0 0 22px rgba(236,72,153,.5)) drop-shadow(0 0 70px rgba(168,85,247,.3)); }
  33% { filter: drop-shadow(0 0 22px rgba(6,182,212,.5)) drop-shadow(0 0 70px rgba(236,72,153,.28)); }
  66% { filter: drop-shadow(0 0 22px rgba(255,184,76,.5)) drop-shadow(0 0 70px rgba(168,85,247,.3)); }
}
@keyframes medusaPromoHalo { 0%,100% { opacity:.5; transform: scale(1) } 50% { opacity:.85; transform: scale(1.07) } }
`;

const FEATURES = [
  { Icon: AudioWaveform, label: "Live Stem Mixer" },
  { Icon: Mic, label: "Snake Pit Recorder" },
  { Icon: Gamepad2, label: "Piano Scales Arcade" },
  { Icon: Sparkles, label: "Daily Petrified Drops" },
];

export const MedusaPromo = () => (
  <section className="relative py-16 sm:py-24 px-5 sm:px-8 overflow-hidden" data-testid="medusa-promo">
    <style>{PROMO_CSS}</style>
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="absolute top-10 -left-24 w-[420px] h-[420px] rounded-full bg-fuchsia-600/10 blur-[130px]" />
      <div className="absolute bottom-0 -right-20 w-[380px] h-[380px] rounded-full bg-crystal/10 blur-[120px]" />
    </div>
    <div className="relative max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
      <div className="relative text-center lg:text-left order-2 lg:order-1">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-fuchsia-400" data-testid="medusa-promo-eyebrow">New · Interactive Experience</p>
        <h2 className="mt-3 font-display text-3xl sm:text-5xl font-bold text-white leading-tight" data-testid="medusa-promo-title">
          Step Inside<br />The <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 via-purple-400 to-teal-300">Medusa Room</span>
        </h2>
        <p className="mt-4 text-sm sm:text-base text-ice/60 leading-relaxed max-w-md mx-auto lg:mx-0">
          Not a page — a living room. Mix beat stems awake from stone, record a 30-second vocal straight to our A&amp;R desk,
          battle the Piano Scales leaderboard for real prizes, and catch the daily drop before it turns to stone at midnight.
        </p>
        <div className="mt-6 flex flex-wrap justify-center lg:justify-start gap-2.5">
          {FEATURES.map(({ Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em] border border-fuchsia-500/35 text-fuchsia-200/90 rounded-full px-3.5 py-1.5" data-testid={`promo-chip-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <Icon size={12} /> {label}
            </span>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-3">
          <Link to="/medusa" className="inline-flex items-center gap-2.5 bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white font-bold text-sm px-8 py-4 rounded-full shadow-[0_0_28px_rgba(236,72,153,0.45)] hover:shadow-[0_0_44px_rgba(168,85,247,0.6)] active:scale-95 transition-all" data-testid="promo-enter-room">
            <Crown size={16} /> Enter the Room
          </Link>
          <Link to="/vip-vault" className="inline-flex items-center gap-2 border border-[#FFB800]/50 text-[#FFB800] font-bold text-sm px-7 py-4 rounded-full hover:bg-[#FFB800] hover:text-black active:scale-95 transition-all" data-testid="promo-enter-vault">
            VIP Vault <ArrowRight size={15} />
          </Link>
        </div>
      </div>
      <div className="relative order-1 lg:order-2 flex justify-center" data-testid="medusa-promo-art">
        <div className="absolute -inset-8 bg-[radial-gradient(circle,rgba(236,72,153,0.22),rgba(168,85,247,0.1)_55%,transparent_75%)] blur-2xl" style={{ animation: "medusaPromoHalo 3.6s ease-in-out infinite" }} />
        <img src="/medusa-logo.webp" alt="The Medusa Room — Cold-Blooded Records interactive experience" className="relative w-56 sm:w-80" style={{ animation: "medusaPromoFloat 5s ease-in-out infinite, medusaPromoGlow 9s ease-in-out infinite" }} />
      </div>
    </div>
  </section>
);
